import { useState, useEffect, useMemo, useRef, useCallback, createContext, useContext, type ReactNode } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import { Navbar } from './Navbar';
import { UserMenu } from './UserMenu';
import { usePractice, useBillingSync } from '../../api/queries';
import { useBranding } from '../../hooks/useBranding';
import { Spinner } from '../common/Spinner';

// Context so child pages can inject action buttons into the practice-heading bar
const HeadingActionsContext = createContext<{
  actions: ReactNode;
  setActions: (node: ReactNode) => void;
}>({ actions: null, setActions: () => {} });

export function useHeadingActions() {
  return useContext(HeadingActionsContext);
}

export function AppShell() {
  const { data: practiceData, isLoading } = usePractice();
  const [actions, setActionsRaw] = useState<ReactNode>(null);
  const setActions = useCallback((node: ReactNode) => setActionsRaw(node), []);
  const headingCtx = useMemo(() => ({ actions, setActions }), [actions, setActions]);
  const [searchParams, setSearchParams] = useSearchParams();
  const billingSync = useBillingSync();
  const billingSynced = useRef(false);

  // Sync billing status when returning from Stripe Checkout
  useEffect(() => {
    if (searchParams.get('billing') === 'success' && !billingSynced.current) {
      billingSynced.current = true;
      billingSync.mutate(undefined, {
        onSettled: () => {
          searchParams.delete('billing');
          setSearchParams(searchParams, { replace: true });
        },
      });
    }
  }, [searchParams, setSearchParams, billingSync]);

  useBranding(practiceData?.practice?.branding);

  // Enrich Datadog RUM sessions with practice/provider context
  useEffect(() => {
    if (!practiceData) return;
    const practice = practiceData.practice;
    const provider = practiceData.provider;
    datadogRum.setGlobalContextProperty('practiceId', practiceData.practiceId);
    datadogRum.setGlobalContextProperty('practiceName', practice?.branding?.practiceName || practice?.name || '');
    datadogRum.setGlobalContextProperty('providerName', provider?.name || '');
    datadogRum.setGlobalContextProperty('providerRole', provider?.role || '');
    datadogRum.setGlobalContextProperty('subscriptionStatus', practice?.subscription?.status || 'none');
    // Enrich the RUM user with provider details not available at auth time
    if (provider) {
      datadogRum.setUser({
        id: provider.uid || '',
        email: provider.email || '',
        name: provider.name || '',
      });
    }
  }, [practiceData]);

  if (isLoading) {
    return <Spinner fullPage />;
  }

  const practice = practiceData?.practice;
  const provider = practiceData?.provider;
  const branding = practice?.branding;

  return (
    <HeadingActionsContext.Provider value={headingCtx}>
      <div style={{ background: '#fff', padding: '0.75rem 1.5rem' }}>
        <div className="container container--wide header-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <img
              src="/img/logo.svg"
              alt="SimplyTelehealth"
              style={{ width: '34px', height: '34px' }}
              id="appLogo"
            />
            <h1 id="appName" style={{ margin: 0, padding: 0, fontSize: '1.25rem' }}>
              SimplyTelehealth
            </h1>
          </div>
          <Navbar />
          <UserMenu provider={provider} />
        </div>
      </div>

      <div id="pageContent" className="container container--wide">
        <div
          className="practice-heading"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1rem' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {branding?.logoUrl && (
              <img
                id="practiceLogo"
                src={branding.logoUrl}
                style={{ maxHeight: '32px', maxWidth: '100px', objectFit: 'contain' }}
                alt="Practice logo"
              />
            )}
            <h2 id="practiceName" style={{ margin: 0, color: 'var(--text-color)', fontWeight: 600 }}>
              {branding?.practiceName || ''}
            </h2>
          </div>
          {actions}
        </div>

        <Outlet />
      </div>
    </HeadingActionsContext.Provider>
  );
}
